import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const PaymentMobileResultPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState({
    success: false,
    message: "",
  });

  useEffect(() => {
    // URL 쿼리 파라미터 파싱
    const queryParams = new URLSearchParams(location.search);
    const processPaymentResult = async () => {
      try {
        setIsLoading(true);

        // 필수 파라미터 체크
        const P_STATUS = queryParams.get("P_STATUS");
        const P_TID = queryParams.get("P_TID");
        const P_RMESG1 = queryParams.get("P_RMESG1");
        const P_AMT = queryParams.get("P_AMT");
        const P_REQ_URL = queryParams.get("P_REQ_URL");
        const idcName = queryParams.get("idc_name");

        // 결제 실패 처리
        if (P_STATUS !== "00") {
          setStatus({
            success: false,
            message: P_RMESG1 || "결제에 실패했습니다.",
          });
          setIsLoading(false);
          return;
        }

        // IDC 센터 코드 검증
        if (idcName) {
          // 운영 환경에 따른 URL 기본 부분 체크
          const validFcUrl = "https://fcmobile.inicis.com";
          const validKsUrl = "https://ksmobile.inicis.com";
          const validStgUrl = "https://stgmobile.inicis.com";

          let isValidUrl = false;
          if (
            (idcName === "fc" && P_REQ_URL.startsWith(validFcUrl)) ||
            (idcName === "ks" && P_REQ_URL.startsWith(validKsUrl)) ||
            (idcName === "stg" && P_REQ_URL.startsWith(validStgUrl))
          ) {
            isValidUrl = true;
          }

          if (!isValidUrl) {
            setStatus({
              success: false,
              message: "승인 요청 URL이 유효하지 않습니다.",
            });
            setIsLoading(false);
            return;
          }
        }

        // 승인 요청
        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
        );
        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const applicationNumber = Cookies.get("application_number");
        if (!applicationNumber) {
          throw new Error("신청번호가 없습니다.");
        }

        // 서버에 결제 완료 요청
        const completeResponse = await fetch(
          `${API_ENDPOINTS.BASE_URL}/payments/complete`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              paymentId: P_TID,
              applicationNumber: applicationNumber,
              price: parseInt(P_AMT, 10),
              payMethod: "MOBILE",
            }),
          }
        );

        if (!completeResponse.ok) {
          const errorData = await completeResponse.json();
          throw new Error(
            errorData.message || "결제 완료 처리에 실패했습니다."
          );
        }

        // 결제 완료 처리 성공
        setStatus({
          success: true,
          message: "결제가 성공적으로 완료되었습니다.",
        });

        // 결제 완료 후 모든 쿠키 삭제
        Object.keys(Cookies.get()).forEach((cookie) => {
          Cookies.remove(cookie);
        });

        // 3초 후 완료 페이지로 이동
        setTimeout(() => {
          navigate("/complete");
        }, 3000);
      } catch (err) {
        setError(err.message || "결제 처리 중 오류가 발생했습니다.");

        // 에러 발생 시 3초 후 결제 방법 선택 페이지로 이동
        setTimeout(() => {
          // navigate("/payment-methods");
          navigate("/payment");
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    processPaymentResult();
  }, [location.search, navigate]);

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content>
          {isLoading ? (
            <LoadingContainer>
              <Spinner />
              <LoadingMessage>결제 정보를 처리 중입니다...</LoadingMessage>
            </LoadingContainer>
          ) : error ? (
            <ErrorContainer>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorTitle>결제 처리 중 오류가 발생했습니다</ErrorTitle>
              <ErrorDescription>{error}</ErrorDescription>
              <RedirectMessage>
                잠시 후 결제 방법 선택 페이지로 이동합니다...
              </RedirectMessage>
            </ErrorContainer>
          ) : (
            <ResultContainer success={status.success}>
              <ResultIcon>{status.success ? "✅" : "❌"}</ResultIcon>
              <ResultTitle>
                {status.success ? "결제 성공" : "결제 실패"}
              </ResultTitle>
              <ResultMessage>{status.message}</ResultMessage>
              <RedirectMessage>
                {status.success
                  ? "잠시 후 완료 페이지로 이동합니다..."
                  : "잠시 후 결제 방법 선택 페이지로 이동합니다..."}
              </RedirectMessage>
            </ResultContainer>
          )}
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: 100vh;
    box-shadow: none;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: 400px;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  min-height: 400px;
`;

// 로딩 관련 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 117, 255, 0.1);
  border-radius: 50%;
  border-left-color: #E31C20;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingMessage = styled.p`
  font-size: 16px;
  color: #666;
  text-align: center;
`;

// 에러 관련 컴포넌트
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const ErrorTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
`;

const ErrorDescription = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 24px 0;
`;

// 결과 관련 컴포넌트
const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
  text-align: center;
`;

const ResultIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const ResultTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
`;

const ResultMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 32px 0;
`;

const RedirectMessage = styled.p`
  font-size: 14px;
  color: #999;
  margin: 0;
  font-style: italic;
`;

export default PaymentMobileResultPage;
